import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { trackConversion } from "@/lib/gtag";
import { MapPin, Phone, Check, ArrowLeft, Plane, Briefcase, Clock, Route as RouteIcon } from "lucide-react";
import { SUBURB_MAP, SUBURBS, indicativeAirportFare, suburbFaq, type Suburb } from "@/lib/suburbs";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { SORTED_POSTS } from "@/lib/blog/posts";

export const Route = createFileRoute("/suburbs/$suburb")({
  loader: ({ params }) => {
    const suburb = SUBURB_MAP[params.suburb];
    if (!suburb) throw notFound();
    return { suburb };
  },
  head: ({ params, loaderData }) => {
    if (!loaderData) {
      return {
        meta: [
          { title: "Suburb not found — SE Premium Cabs" },
          { name: "robots", content: "noindex" },
        ],
      };
    }
    const { suburb } = loaderData;
    const isAirportSuburb = suburb.airportKm === 0;
    const title = isAirportSuburb
      ? `Melbourne Airport Taxi & Premium Cabs (T1–T4) — SE Premium Cabs`
      : `Premium Taxi Service ${suburb.name} — Cabs & Airport Transfers`;
    const description = isAirportSuburb
      ? `Premium chauffeured cabs at Melbourne Airport, all four terminals. Flight tracking, meet & greet, Sedan, SUV and Maxi. Book online or call 0493 100 265.`
      : `Premium taxi service in ${suburb.name} VIC ${suburb.postcode}. Melbourne Airport in about ${suburb.airportMin} min. Sedan, SUV & Maxi, 24/7. Call 0493 100 265.`;
    const url = `https://sepremiumcabs.com/suburbs/${params.suburb}`;
    const faq = suburbFaq(suburb);

    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
        { name: "twitter:card", content: "summary_large_image" },
        { property: "og:image", content: "https://sepremiumcabs.com/og-image.jpg" },
        { name: "twitter:image", content: "https://sepremiumcabs.com/og-image.jpg" },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "TaxiService",
            name: `SE Premium Cabs — ${suburb.name}`,
            description,
            telephone: "+61493100265",
            url,
            areaServed: {
              "@type": "Place",
              name: `${suburb.name}, Victoria ${suburb.postcode}`,
            },
            provider: {
              "@type": "LocalBusiness",
              name: "SE Premium Cabs",
              url: "https://sepremiumcabs.com",
              telephone: "+61493100265",
              email: "sepremiumcabs@gmail.com",
              image: "https://sepremiumcabs.com/og-image.jpg",
            },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faq.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: "https://sepremiumcabs.com/" },
              {
                "@type": "ListItem",
                position: 2,
                name: "Suburbs",
                item: "https://sepremiumcabs.com/suburbs",
              },
              { "@type": "ListItem", position: 3, name: suburb.name, item: url },
            ],
          }),
        },
      ],
    };
  },
  notFoundComponent: () => (
    <div className="flex min-h-screen items-center justify-center bg-background text-foreground">
      <div className="text-center">
        <h1 className="text-2xl font-bold">Suburb not found</h1>
        <Link to="/suburbs" className="mt-4 inline-block text-primary underline">
          View all suburbs
        </Link>
      </div>
    </div>
  ),
  component: SuburbPage,
});

/** Blog guides that mention this suburb by name, for internal linking. */
function relatedGuides(suburb: Suburb) {
  const needle = suburb.name.toLowerCase();
  return SORTED_POSTS.filter(
    (p) =>
      p.title.toLowerCase().includes(needle) ||
      p.keyword.toLowerCase().includes(needle) ||
      p.excerpt.toLowerCase().includes(needle),
  ).slice(0, 3);
}

function SuburbPage() {
  const { suburb } = Route.useLoaderData();
  const nearby = suburb.nearby
    .map((slug) => SUBURB_MAP[slug])
    .filter((s): s is Suburb => Boolean(s));
  const others = SUBURBS.filter(
    (s) => s.slug !== suburb.slug && !suburb.nearby.includes(s.slug),
  ).slice(0, 8);
  const sedan = indicativeAirportFare(suburb, "Premium Sedan");
  const suv = indicativeAirportFare(suburb, "Premium SUV");
  const maxi = indicativeAirportFare(suburb, "Maxi");
  const faq = suburbFaq(suburb);
  const guides = relatedGuides(suburb);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader active="suburbs" />

      <main className="mx-auto max-w-[1400px] px-6 py-10">
        <Link
          to="/suburbs"
          className="inline-flex items-center gap-1 text-xs font-semibold tracking-widest text-primary hover:underline"
        >
          <ArrowLeft className="h-3 w-3" /> ALL SUBURBS
        </Link>

        <div className="mt-4 flex items-center gap-2 text-xs font-semibold tracking-[0.3em] text-primary">
          <MapPin className="h-4 w-4" /> {suburb.name.toUpperCase()} · VIC {suburb.postcode}
        </div>
        <h1 className="mt-3 text-4xl font-bold tracking-tight md:text-5xl">
          Premium Taxi Service in {suburb.name}
        </h1>
        <p className="mt-4 max-w-3xl text-muted-foreground">{suburb.blurb}</p>
        <p className="mt-3 max-w-3xl text-muted-foreground">{suburb.localNote}</p>

        <div className="mt-6 flex flex-wrap gap-3">
          <Link
            to="/"
            hash="book"
            className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground"
          >
            Book a ride
          </Link>
          <a
            href="tel:0493100265"
            onClick={() => trackConversion("phoneCall")}
            className="inline-flex items-center gap-2 rounded-full border border-primary px-5 py-2.5 text-sm font-semibold text-primary"
          >
            <Phone className="h-4 w-4" /> Call 0493 100 265
          </a>
        </div>

        <section className="mt-10 grid grid-cols-1 gap-4 md:grid-cols-3">
          {suburb.highlights.map((h: string) => (
            <div
              key={h}
              className="flex items-start gap-3 rounded-lg border border-border/60 bg-secondary/40 p-4"
            >
              <Check className="mt-0.5 h-5 w-5 text-primary" />
              <span className="text-sm">{h}</span>
            </div>
          ))}
        </section>

        {suburb.airportKm > 0 && (
          <section className="mt-12 rounded-lg border border-border/60 bg-secondary/40 p-6">
            <h2 className="flex items-center gap-2 text-xl font-semibold">
              <RouteIcon className="h-5 w-5 text-primary" />
              {suburb.name} to Melbourne Airport
            </h2>
            <div className="mt-5 grid grid-cols-2 gap-4 md:grid-cols-4">
              <Stat label="Distance" value={`~${suburb.airportKm} km`} />
              <Stat label="Drive time" value={`~${suburb.airportMin} min`} />
              <Stat label="To the CBD" value={`~${suburb.cbdMin} min`} />
              <Stat label="Route" value={suburb.airportRoute.split(",")[0]} />
            </div>

            {sedan && suv && maxi && (
              <>
                <h3 className="mt-8 text-sm font-semibold tracking-widest text-primary">
                  INDICATIVE AIRPORT FARES (DAY RATE)
                </h3>
                <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
                  <FareCard vehicle="Premium Sedan" seats="Up to 4" fare={sedan} />
                  <FareCard vehicle="Premium SUV" seats="Up to 6" fare={suv} />
                  <FareCard vehicle="Maxi" seats="Up to 11" fare={maxi} />
                </div>
                <p className="mt-3 text-xs text-muted-foreground">
                  Estimates based on the published Victorian metered rates for a day-rate trip.
                  Overnight (5pm–9am) and peak rates are higher, and tolls or airport fees may apply.
                  Get your exact quote when you book.
                </p>
              </>
            )}

            <p className="mt-6 text-sm text-muted-foreground">
              Flying out of or into Tullamarine?{" "}
              <Link to="/melbourne-airport-taxi" className="text-primary underline">
                See our Melbourne Airport taxi &amp; transfer service
              </Link>{" "}
              for flight tracking and terminal meet &amp; greet.
            </p>
          </section>
        )}

        <section className="mt-12 grid grid-cols-1 gap-6 lg:grid-cols-3">
          <ServiceCard
            icon={Plane}
            title={`${suburb.name} Airport Transfers`}
            body={`Pre-booked transfers between ${suburb.name} and Melbourne (Tullamarine) and Avalon airports. Flight tracking and meet-and-greet available.`}
          />
          <ServiceCard
            icon={Briefcase}
            title="Corporate Travel"
            body={`Executive Mercedes sedans and SUVs for business meetings, client pickups and roadshows across ${suburb.name} and surrounds.`}
          />
          <ServiceCard
            icon={Clock}
            title="24/7 Local Rides"
            body={`On-demand and pre-booked cabs in ${suburb.name} — day or night. Sedans, SUVs and Maxi taxis for groups up to 11.`}
          />
        </section>

        <section className="mt-12 grid grid-cols-1 gap-6 lg:grid-cols-2">
          <div className="rounded-lg border border-border/60 bg-secondary/40 p-6">
            <h2 className="text-xl font-semibold">Pickup points we know in {suburb.name}</h2>
            <ul className="mt-4 space-y-2">
              {suburb.landmarks.map((l) => (
                <li key={l} className="flex items-start gap-2 text-sm">
                  <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                  <span className="text-muted-foreground">{l}</span>
                </li>
              ))}
            </ul>
            <p className="mt-4 text-sm text-muted-foreground">{suburb.pickupNote}</p>
          </div>

          <div className="rounded-lg border border-border/60 bg-secondary/40 p-6">
            <h2 className="text-xl font-semibold">
              Why choose SE Premium Cabs in {suburb.name}?
            </h2>
            <ul className="mt-4 space-y-3">
              {[
                "Late-model Mercedes and premium SUV fleet",
                "Professional, background-checked chauffeurs",
                "Upfront fare estimate before you confirm",
                "24/7 booking — online or by phone",
                "Baby & booster seats on request",
                "Corporate accounts with monthly invoicing",
              ].map((item) => (
                <li key={item} className="flex items-start gap-2 text-sm">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                  <span className="text-muted-foreground">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="mt-12">
          <h2 className="text-xl font-semibold">
            {suburb.name} cab questions, answered
          </h2>
          <div className="mt-4 space-y-3">
            {faq.map((f) => (
              <details
                key={f.q}
                className="rounded-lg border border-border/60 bg-secondary/40 p-4"
              >
                <summary className="cursor-pointer text-sm font-semibold">{f.q}</summary>
                <p className="mt-2 text-sm text-muted-foreground">{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        {guides.length > 0 && (
          <section className="mt-12">
            <h2 className="text-xl font-semibold">Guides for {suburb.name} travellers</h2>
            <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-3">
              {guides.map((g) => (
                <Link
                  key={g.slug}
                  to="/blog/$slug"
                  params={{ slug: g.slug }}
                  className="rounded-lg border border-border/60 bg-secondary/40 p-4 hover:border-primary"
                >
                  <div className="text-sm font-semibold">{g.title}</div>
                  <p className="mt-2 text-xs text-muted-foreground">{g.excerpt}</p>
                </Link>
              ))}
            </div>
          </section>
        )}

        <section className="mt-12">
          <h2 className="text-xl font-semibold">Nearby suburbs we also cover</h2>
          <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
            {nearby.map((s) => (
              <Link
                key={s.slug}
                to="/suburbs/$suburb"
                params={{ suburb: s.slug }}
                className="rounded-md border border-border/60 bg-secondary/40 px-3 py-2 text-center text-sm hover:border-primary"
              >
                Cabs in {s.name}
              </Link>
            ))}
          </div>

          <h3 className="mt-8 text-sm font-semibold tracking-widest text-primary">
            ELSEWHERE IN MELBOURNE
          </h3>
          <div className="mt-3 flex flex-wrap gap-2">
            {others.map((s) => (
              <Link
                key={s.slug}
                to="/suburbs/$suburb"
                params={{ suburb: s.slug }}
                className="rounded-full border border-border/60 px-3 py-1.5 text-xs text-muted-foreground hover:border-primary hover:text-primary"
              >
                {s.name}
              </Link>
            ))}
            <Link
              to="/suburbs"
              className="rounded-full border border-primary px-3 py-1.5 text-xs font-semibold text-primary"
            >
              View all suburbs
            </Link>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-border/60 bg-background p-3">
      <div className="text-[10px] font-semibold tracking-widest text-muted-foreground">
        {label.toUpperCase()}
      </div>
      <div className="mt-1 text-sm font-semibold">{value}</div>
    </div>
  );
}

function FareCard({
  vehicle,
  seats,
  fare,
}: {
  vehicle: string;
  seats: string;
  fare: { low: number; high: number };
}) {
  return (
    <div className="rounded-md border border-border/60 bg-background p-4">
      <div className="text-sm font-semibold">{vehicle}</div>
      <div className="text-xs text-muted-foreground">{seats} passengers</div>
      <div className="mt-2 text-lg font-bold text-primary">
        ${fare.low}–${fare.high}
      </div>
    </div>
  );
}

function ServiceCard({
  icon: Icon,
  title,
  body,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  body: string;
}) {
  return (
    <div className="rounded-lg border border-border/60 bg-secondary/40 p-5">
      <Icon className="h-6 w-6 text-primary" />
      <h3 className="mt-3 font-semibold">{title}</h3>
      <p className="mt-2 text-sm text-muted-foreground">{body}</p>
    </div>
  );
}
